import baustein from "baustein";
import _ from "underscore";
import rothko from "web/script/modules/rothko";
import template from "templates/modules/recently_viewed.jinja";
import { addVisibilityWatcher } from "web/script/modules/run-loop";
import userProfiler from "web/script/modules/userprofiler";
import history from "web/script/utils/history";
import browser from "web/script/utils/browser";
import PaginatedCarousel from "web/script/components/paginated-carousel";
import { gettext } from "web/script/modules/django-i18n";

import "web/script/components/product-card";

const MOBILE_SET = 2.5;
const TABLET_SET = 3.2;
const DESKTOP_SET = 5;
const GUTTER_WIDTH = 40;
const TABLET_GUTTER_WIDTH = 30;
const MOBILE_GUTTER_WIDTH = 20;

const PRODUCT_CARD_SELECTOR = ".product-card";
const CONTAINER_SELECTOR = ".recently-viewed__container";
const RECENTLY_VIEWED_CAROUSEL_ENABLED_CLASS = "recently-viewed--carousel-enabled";

export default baustein.register("recently-viewed", {
    template: template,

    defaultOptions: {
        inOverlay: false,
    },

    setupEvents(add) {
        add("openOverlay", "product-card", this.openProductOverlay);
        add("destroyCarousel", "paginated-carousel", this.destroy);
        this.setGlobalHandler("resize", this._onResize);
    },

    onInsert() {
        this._removeVisiblityWatcher = addVisibilityWatcher(this.el, (visible) => {
            if (visible) {
                this.fetchAndRender();
                this._removeVisiblityWatcher();
            }
        });
    },

    onRemove() {
        if (this._removeVisiblityWatcher) {
            this._removeVisiblityWatcher();
        }
    },

    fetchAndRender() {
        const recentlyViewedProducts = this._getRecentlySeenProductIds(10);

        if (recentlyViewedProducts.length === 0) {
            this.destroy();
            return;
        }

        this.render();

        // To make testing possible, we're returning the
        // promise in this function.
        return rothko
            .fetch("modules/recently_viewed", this.makeApiParams(recentlyViewedProducts))
            .then((data) => {
                if (_.isEmpty(data.products)) {
                    this.destroy();
                    return;
                }

                this.setRenderContext({
                    recently_viewed: data,
                });
                this._updateCarousel();
            })
            .catch((err) => {
                console.error("Error fetching recently viewed products:", err);
                this.destroy();
            });
    },

    /**
     * Builds the API parameters to pass to the Rothko call.
     * Omits the current_product_id key if we don't have one.
     *
     * @params {Array} recentlyViewedProducts - the recently viewed products array.
     * @returns {Object}
     */
    makeApiParams(recentlyViewedProducts) {
        return _.omit(
            {
                product_id: recentlyViewedProducts,
                in_overlay: this.options.inOverlay,
            },
            (value) => {
                return _.isUndefined(value);
            }
        );
    },

    /**
     * Gets the initial render context.
     *
     * @returns {Object}
     */
    getInitialRenderContext() {
        return {
            recently_viewed: {
                // 'Your recently viewed products are loading...'
                title: gettext("product.recently_viewed.title_loading"),
                is_loading: true,
            },
        };
    },

    /**
     * Gets the last seen product ids.
     *
     * @params {Number} number - The number of product we want to retrieve
     * @returns {Array} productIds - An array containing the product ids
     */
    _getRecentlySeenProductIds(number) {
        const currentProductId = this.options.currentProductId;

        // If we're on a product page we're going to exclude
        // the current product from the recently viewed module.
        if (currentProductId) {
            number++;
        }

        let seenProducts = userProfiler
            .getSeenProducts()
            .slice(-number)
            .reverse();
        let productIds = seenProducts.reduce((memo, product) => {
            if (product.productId !== currentProductId) {
                memo.push(product.productId);
            }

            return memo;
        }, []);

        return productIds;
    },

    openProductOverlay(event) {
        history.replaceState(event.url, event.queryParams);
    },

    /**
     * Recalculate the carousel options when the browser is resized
     */
    _onResize: _.throttle(function() {
        this._updateCarousel();
    }, 250),

    /**
     * If it doesn't exist create a carousel else, update the carousel options
     */
    _updateCarousel() {
        const container = this.el.querySelector(CONTAINER_SELECTOR);
        if (!container) {
            return;
        }
        const [gutterWidth, itemsPerPage] = this._getItemLayout();
        const options = {
            items: Array.from(this.el.querySelectorAll(PRODUCT_CARD_SELECTOR)),
            gutterWidth,
            itemsPerPage,
            eventCategory: "product-cards",
        };

        let paginatedCarousel = this.findComponent("paginated-carousel");
        if (paginatedCarousel) {
            paginatedCarousel.updateOptions(options);
        } else {
            paginatedCarousel = new PaginatedCarousel(options);
            paginatedCarousel.appendTo(container);
        }

        this.el.classList.add(RECENTLY_VIEWED_CAROUSEL_ENABLED_CLASS);
    },

    /**
     * Identify how many items we want to show (based on viewport width) and the left
     * and right padding each item should have
     */
    _getItemLayout() {
        if (browser.getViewport() <= browser.VIEWPORTS.MOBILE) {
            return [MOBILE_GUTTER_WIDTH, MOBILE_SET];
        } else if (browser.getViewport() <= browser.VIEWPORTS.TABLET) {
            return [TABLET_GUTTER_WIDTH, TABLET_SET];
        } else {
            return [GUTTER_WIDTH, DESKTOP_SET];
        }
    },
});
